import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Header } from '../components/Layout';
import { getLeads, getLead, markAllLeadsSeen, convertLeadToQuote, assignLead, redistributeLeads, reassignAllLeadsToMe, getUsers } from '../lib/api';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Search, Eye, ChevronLeft, ChevronRight, Users, ArrowRightCircle, Car, FileText, Plus, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-gray-100 text-gray-700' },
  { value: 'quoted', label: 'Quoted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'follow_up_needed', label: 'Follow-up Needed', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'ai_calling', label: 'AI Calling', color: 'bg-blue-100 text-blue-700' },
  { value: 'no_answer', label: 'No Answer', color: 'bg-gray-100 text-gray-700' },
  { value: 'voicemail_left', label: 'Voicemail Left', color: 'bg-blue-100 text-blue-700' },
  { value: 'interested', label: 'Interested', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'callback_scheduled', label: 'Callback Scheduled', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-gray-100 text-gray-700' },
  { value: 'do_not_call', label: 'Do Not Call', color: 'bg-red-100 text-red-700' },
  { value: 'booked', label: 'Booked', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
  { value: 'wrong_number', label: 'Wrong Number', color: 'bg-red-100 text-red-700' },
  { value: 'lead', label: 'Lead (Legacy)', color: 'bg-gray-100 text-gray-700' },
];

const getStatusInfo = (status) => LEAD_STATUSES.find(s => s.value === status) || { label: status || 'New', color: 'bg-gray-100 text-gray-700' };

const Leads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { fetchSidebarCounts } = useNotifications() || {};
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(100);
  const [quickView, setQuickView] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [redistributing, setRedistributing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * pageSize, limit: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (agentFilter) params.assigned_to_id = agentFilter;
      const res = await getLeads(params);
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch { toast.error('Failed to fetch leads'); }
    finally { setLoading(false); }
  }, [page, pageSize, search, statusFilter, agentFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mark all leads as seen when page loads, clearing the sidebar badge
  useEffect(() => {
    markAllLeadsSeen().then(() => {
      if (fetchSidebarCounts) fetchSidebarCounts();
    }).catch(() => {});
  }, [fetchSidebarCounts]);

  // Fetch users list for admin/superadmin (for reassign)
  useEffect(() => {
    if (isAdmin) {
      getUsers().then(res => setUsersList(res.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Reset filters and refetch on sidebar click
  useEffect(() => {
    if (location.state?.refreshKey) {
      setSearch('');
      setSearchInput('');
      setPage(0);
      if (fetchSidebarCounts) fetchSidebarCounts();
    }
  }, [location.state?.refreshKey, fetchSidebarCounts]);

  const handleSearch = () => { setSearch(searchInput); setPage(0); };

  const handleConvertToQuote = async (lead) => {
    try {
      await convertLeadToQuote(lead.id);
      toast.success(`Lead ${lead.quote_number} converted to quote!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleRedistribute = async () => {
    setRedistributing(true);
    try {
      const res = await redistributeLeads();
      toast.success(res.data.message || `${res.data.distributed} leads distributed`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redistribute');
    } finally { setRedistributing(false); }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Layout>
      <Header title="Leads">
        {isSuperAdmin && (
          <Button onClick={handleRedistribute} disabled={redistributing} variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="redistribute-leads-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${redistributing ? 'animate-spin' : ''}`} />
            {redistributing ? 'Distributing...' : 'Auto-Distribute'}
          </Button>
        )}
        <Button onClick={() => { fetchData(); toast.success('Leads refreshed'); }} variant="outline" data-testid="refresh-leads-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
        <Button onClick={() => navigate('/leads/new')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-lead-btn">
          <Plus className="w-4 h-4 mr-2" /> New Lead
        </Button>
      </Header>
      <div className="p-6" data-testid="leads-page">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-5">
          <div className="flex gap-3">
            <div className="flex-1 flex gap-2">
              <Input placeholder="Search name, phone, email, city..." value={searchInput}
                onChange={e => setSearchInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSearch()}
                data-testid="search-leads" />
              <Button onClick={handleSearch} variant="outline"><Search className="w-4 h-4" /></Button>
            </div>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {LEAD_STATUSES.filter(s => s.value !== 'lead').map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={agentFilter} onValueChange={v => { setAgentFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="agent-filter"><SelectValue placeholder="All Agents" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {usersList.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-slate-600" data-testid="leads-count">Showing {leads.length} of {total.toLocaleString()} leads</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(0)} disabled={page === 0}>First</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm text-slate-600 min-w-[100px] text-center">Page {page + 1} of {totalPages || 1}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</Button>
          </div>
        </div>
        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12" data-testid="no-leads"><Users className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No leads yet. New vendor leads will appear here.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">ID</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Customer</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Phone</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vehicle</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Pickup</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Delivery</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Miles</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Assigned To</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Source</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map(lead => (
                    <tr key={lead.id} className="hover:bg-slate-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-3 py-2.5"><span className="font-mono text-xs font-semibold text-blue-600">{lead.quote_number}</span></td>
                      <td className="px-3 py-2.5 font-medium text-slate-900 text-xs">{lead.customer_name}</td>
                      <td className="px-3 py-2.5 text-xs">
                        <div className="flex items-center gap-1">
                          {lead.phone_valid === false && <AlertTriangle className="w-3 h-3 text-red-500" title="Invalid phone number" />}
                          <span className="text-slate-600">{lead.phone || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        <button onClick={() => window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${lead.vehicle_year} ${lead.vehicle_make} ${lead.vehicle_model}`)}`, '_blank')}
                          className="text-blue-600 hover:underline cursor-pointer">
                          {[lead.vehicle_year, lead.vehicle_make, lead.vehicle_model].filter(Boolean).join(' ') || '-'}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-700">{[lead.pickup_city, lead.pickup_state].filter(Boolean).join(', ') || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-700">{[lead.delivery_city, lead.delivery_state].filter(Boolean).join(', ') || '-'}</td>
                      <td className="px-3 py-2.5 text-center text-xs">
                        {lead.estimated_distance ? (
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-semibold">{lead.estimated_distance.toLocaleString()} mi</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {isAdmin ? (
                          <select
                            className="bg-transparent border border-slate-200 rounded-md px-2 py-1 text-xs font-medium text-emerald-700 cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none min-w-[120px]"
                            value={lead.assigned_to_id || ''}
                            onChange={async (e) => {
                              const newUserId = e.target.value;
                              if (!newUserId || newUserId === lead.assigned_to_id) return;
                              try {
                                await assignLead(lead.id, { assigned_to_id: newUserId });
                                toast.success('Lead reassigned');
                                fetchData();
                              } catch { toast.error('Failed to reassign'); }
                            }}
                            data-testid={`reassign-select-${lead.id}`}
                          >
                            <option value="" disabled>Unassigned</option>
                            {usersList.map(u => (
                              <option key={u.id} value={u.id}>{u.full_name}</option>
                            ))}
                          </select>
                        ) : lead.agent_name ? (
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">{lead.agent_name}</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">Unassigned</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{lead.source || '-'}</td>
                      <td className="px-3 py-2.5 text-xs">
                        {(() => { const si = getStatusInfo(lead.status); return (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${si.color}`} data-testid={`lead-status-${lead.id}`}>{si.label}</span>
                        ); })()}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600"
                            onClick={() => { setQuickView(lead); getLead(lead.id).then(() => { if (fetchSidebarCounts) fetchSidebarCounts(); }).catch(() => {}); }} title="Quick View" data-testid={`lead-quick-view-${lead.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-blue-600"
                            onClick={() => { getLead(lead.id).then(() => { if (fetchSidebarCounts) fetchSidebarCounts(); }).catch(() => {}); navigate(`/leads/${lead.id}`); }} title="Full Details">
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            onClick={() => handleConvertToQuote(lead)} title="Approve & Convert to Quote" data-testid={`convert-quote-${lead.id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick View */}
      <Dialog open={!!quickView} onOpenChange={() => setQuickView(null)}>
        <DialogContent className="max-w-lg" data-testid="lead-quick-view-modal">
          <DialogHeader><DialogTitle>Lead {quickView?.quote_number}</DialogTitle></DialogHeader>
          {quickView && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Customer:</span> <span className="font-medium">{quickView.customer_name}</span></div>
                <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{quickView.phone || '-'}</span></div>
                <div><span className="text-slate-500">Email:</span> <span className="font-medium">{quickView.email || '-'}</span></div>
                <div><span className="text-slate-500">Source:</span> <span className="font-medium">{quickView.source || '-'}</span></div>
                <div><span className="text-slate-500">Vehicle:</span> <span className="font-medium">{[quickView.vehicle_year, quickView.vehicle_make, quickView.vehicle_model].filter(Boolean).join(' ') || '-'}</span></div>
                <div><span className="text-slate-500">Pickup:</span> <span className="font-medium">{quickView.pickup_address || [quickView.pickup_city, quickView.pickup_state].filter(Boolean).join(', ') || '-'}</span></div>
                <div><span className="text-slate-500">Delivery:</span> <span className="font-medium">{quickView.delivery_address || [quickView.delivery_city, quickView.delivery_state].filter(Boolean).join(', ') || '-'}</span></div>
                <div><span className="text-slate-500">Assigned:</span> <span className="font-medium">{quickView.agent_name || 'Unassigned'}</span></div>
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button onClick={() => { setQuickView(null); navigate(`/leads/${quickView.id}`); }} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                  View Full Details
                </Button>
                <Button onClick={() => { handleConvertToQuote(quickView); setQuickView(null); }} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="quick-convert-quote">
                  <CheckCircle className="w-4 h-4 mr-1" />Approve
                </Button>
                <Button variant="outline" onClick={() => window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${quickView.vehicle_year} ${quickView.vehicle_make} ${quickView.vehicle_model}`)}`, '_blank')}>
                  <Car className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default Leads;
